import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Button } from '../../components/ui/button';
import { Alert } from '../../components/ui/feedback';
import { Field, Input } from '../../components/ui/form';
import { errorMessage, fieldErrors, useAuthStatus, useSetup } from '../../lib/session';
import { AuthLayout } from './auth-layout';

/** First-run screen: creates the owner account, guarded by the deployment's setup token. */
export function SetupPage() {
  const status = useAuthStatus();
  const setup = useSetup();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', workspaceName: 'My workspace', setupToken: '' });

  if (status.data && !status.data.setupRequired) return <Navigate to="/login" replace />;

  const errors = fieldErrors(setup.error);
  const update = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setup.mutate(form, {
      onSuccess: () => {
        void navigate('/', { replace: true });
      },
    });
  };

  return (
    <AuthLayout title="Create your account" description="This is the first account for this deployment — it becomes the owner.">
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {setup.error ? <Alert tone="error">{errorMessage(setup.error)}</Alert> : null}
        <Field label="Your name" error={errors['name']} required>
          {({ id, describedBy, invalid }) => (
            <Input id={id} value={form.name} onChange={update('name')} invalid={invalid} aria-describedby={describedBy} autoFocus required />
          )}
        </Field>
        <Field label="Email" error={errors['email']} required>
          {({ id, describedBy, invalid }) => (
            <Input id={id} type="email" autoComplete="email" value={form.email} onChange={update('email')} invalid={invalid} aria-describedby={describedBy} required />
          )}
        </Field>
        <Field label="Password" hint="At least 10 characters." error={errors['password']} required>
          {({ id, describedBy, invalid }) => (
            <Input id={id} type="password" autoComplete="new-password" value={form.password} onChange={update('password')} invalid={invalid} aria-describedby={describedBy} required />
          )}
        </Field>
        <Field label="Workspace name" error={errors['workspaceName']}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} value={form.workspaceName} onChange={update('workspaceName')} invalid={invalid} aria-describedby={describedBy} />
          )}
        </Field>
        <Field
          label="Setup token"
          hint="From your .dev.vars (local) or the SETUP_TOKEN secret (deployed)."
          error={errors['setupToken']}
          required
        >
          {({ id, describedBy, invalid }) => (
            <Input id={id} value={form.setupToken} onChange={update('setupToken')} invalid={invalid} aria-describedby={describedBy} required />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={setup.isPending}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
